/**
 * Core API - Scrap Entries
 *
 * Database operations for daily scrap/loss tracking per production line.
 * Workers enter scrap data including loss amount, article, station blamed, and comments.
 */

import { supabase } from '@/integrations/supabase/client';

// =============================================================================
// Types
// =============================================================================

export interface ScrapEntry {
  id: string;
  tenant_id: string;
  factory_id: string;
  entry_date: string;
  loss_amount: number;
  area_id: string | null;
  line_id: string | null;
  station_id: string | null;
  article_id: string | null;
  comment: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface ScrapEntryWithRelations extends ScrapEntry {
  area?: { id: string; name: string } | null;
  line?: { id: string; name: string } | null;
  station?: { id: string; name: string } | null;
  article?: { id: string; code: string; name: string | null } | null;
  created_by_profile?: { id: string; full_name: string; email: string } | null;
}

export interface CreateScrapEntryInput {
  tenant_id: string;
  factory_id: string;
  entry_date: string;
  loss_amount: number;
  area_id?: string | null;
  line_id?: string | null;
  station_id?: string | null;
  article_id?: string | null;
  comment?: string | null;
  created_by?: string | null;
}

export interface DailyScrapSummary {
  entry_date: string;
  total_loss: number;
  entry_count: number;
  has_entries: boolean;
}

// =============================================================================
// Queries
// =============================================================================

/**
 * Fetches scrap entries for a specific date and factory.
 */
export async function fetchScrapEntriesByDate(
  factoryId: string,
  entryDate: string
): Promise<ScrapEntryWithRelations[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('scrap_entries')
    .select(`
      *,
      area:factory_areas(id, name),
      line:production_lines(id, name),
      station:stations(id, name),
      article:articles(id, code, name),
      created_by_profile:profiles!scrap_entries_created_by_fkey(id, full_name, email)
    `)
    .eq('factory_id', factoryId)
    .eq('entry_date', entryDate)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data as unknown as ScrapEntryWithRelations[];
}

/**
 * Fetches scrap entries for a specific line and date.
 */
export async function fetchScrapEntriesByLine(
  factoryId: string,
  entryDate: string,
  lineId: string
): Promise<ScrapEntryWithRelations[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('scrap_entries')
    .select(`
      *,
      area:factory_areas(id, name),
      line:production_lines(id, name),
      station:stations(id, name),
      article:articles(id, code, name),
      created_by_profile:profiles!scrap_entries_created_by_fkey(id, full_name, email)
    `)
    .eq('factory_id', factoryId)
    .eq('entry_date', entryDate)
    .eq('line_id', lineId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data as unknown as ScrapEntryWithRelations[];
}

/**
 * Fetches scrap entries for a specific area and date.
 */
export async function fetchScrapEntriesByArea(
  factoryId: string,
  entryDate: string,
  areaId: string
): Promise<ScrapEntryWithRelations[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('scrap_entries')
    .select(`
      *,
      area:factory_areas(id, name),
      line:production_lines(id, name),
      station:stations(id, name),
      article:articles(id, code, name),
      created_by_profile:profiles!scrap_entries_created_by_fkey(id, full_name, email)
    `)
    .eq('factory_id', factoryId)
    .eq('entry_date', entryDate)
    .eq('area_id', areaId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data as unknown as ScrapEntryWithRelations[];
}

/**
 * Fetches scrap entries for a date range (for analytics/reporting).
 */
export async function fetchScrapEntriesByDateRange(
  factoryId: string,
  startDate: string,
  endDate: string
): Promise<ScrapEntryWithRelations[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('scrap_entries')
    .select(`
      *,
      area:factory_areas(id, name),
      line:production_lines(id, name),
      station:stations(id, name),
      article:articles(id, code, name)
    `)
    .eq('factory_id', factoryId)
    .gte('entry_date', startDate)
    .lte('entry_date', endDate)
    .order('entry_date', { ascending: false })
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data as unknown as ScrapEntryWithRelations[];
}

/**
 * Fetches daily summaries for a date range (for gamification calendar).
 * Returns which dates have entries and the total loss per day.
 */
export async function fetchDailySummaries(
  factoryId: string,
  startDate: string,
  endDate: string
): Promise<DailyScrapSummary[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('scrap_entries')
    .select('entry_date, loss_amount')
    .eq('factory_id', factoryId)
    .gte('entry_date', startDate)
    .lte('entry_date', endDate);

  if (error) throw error;

  // Group by date and calculate summaries
  const summaryMap = new Map<string, { total: number; count: number }>();
  
  data.forEach((entry) => {
    const existing = summaryMap.get(entry.entry_date) || { total: 0, count: 0 };
    existing.total += entry.loss_amount;
    existing.count += 1;
    summaryMap.set(entry.entry_date, existing);
  });

  return Array.from(summaryMap.entries()).map(([date, { total, count }]) => ({
    entry_date: date,
    total_loss: total,
    entry_count: count,
    has_entries: true,
  }));
}

/**
 * Checks if entries exist for a specific date.
 */
export async function hasEntriesForDate(
  factoryId: string,
  entryDate: string
): Promise<boolean> {
  const { data, error } = await supabase
    .schema('core')
    .from('scrap_entries')
    .select('id')
    .eq('factory_id', factoryId)
    .eq('entry_date', entryDate)
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return !!data;
}

// =============================================================================
// Mutations
// =============================================================================

/**
 * Creates a new scrap entry.
 */
export async function createScrapEntry(input: CreateScrapEntryInput): Promise<ScrapEntry> {
  const { data, error } = await supabase
    .schema('core')
    .from('scrap_entries')
    .insert(input)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Updates an existing scrap entry.
 */
export async function updateScrapEntry(
  id: string,
  updates: Partial<Omit<CreateScrapEntryInput, 'tenant_id' | 'factory_id'>>
): Promise<ScrapEntry> {
  const { data, error } = await supabase
    .schema('core')
    .from('scrap_entries')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Deletes a scrap entry (hard delete).
 */
export async function deleteScrapEntry(id: string): Promise<void> {
  const { error } = await supabase
    .schema('core')
    .from('scrap_entries')
    .delete()
    .eq('id', id);

  if (error) throw error;
}
